import { isClassProvider, isFactoryProvider, isNormalToken, isTokenProvider, isValueProvider } from "./providers";
import { isTokenDescriptor } from "./providers/injection-token";
import Registry from "./registry";
export var typeInfo = new Map();
var InternalDependencyContainer = (function () {
    function InternalDependencyContainer(parent) {
        this.parent = parent;
        this._registry = new Registry();
    }
    InternalDependencyContainer.prototype.register = function (token, provider, options) {
        if (options === void 0) { options = { singleton: false }; }
        if (options.singleton) {
            if (isValueProvider(provider) || isFactoryProvider(provider)) {
                throw "Cannot use {singleton: true} with ValueProviders or FactoryProviders";
            }
        }
        this._registry.set(token, { provider: provider, options: options });
        return this;
    };
    InternalDependencyContainer.prototype.registerType = function (from, to) {
        if (isNormalToken(to)) {
            return this.register(from, {
                useToken: to
            });
        }
        return this.register(from, {
            useClass: to
        });
    };
    InternalDependencyContainer.prototype.registerInstance = function (token, instance) {
        return this.register(token, {
            useValue: instance
        });
    };
    InternalDependencyContainer.prototype.registerSingleton = function (from, to) {
        if (isNormalToken(from)) {
            if (isNormalToken(to)) {
                return this.register(from, {
                    useToken: to
                }, { singleton: true });
            }
            else if (to) {
                return this.register(from, {
                    useClass: to
                }, { singleton: true });
            }
            throw "Cannot register a type name as a singleton without a \"to\" token";
        }
        var useClass = from;
        if (to && !isNormalToken(to)) {
            useClass = to;
        }
        return this.register(from, {
            useClass: useClass
        }, { singleton: true });
    };
    InternalDependencyContainer.prototype.resolve = function (token) {
        var registration = this.getRegistration(token);
        if (!registration && isNormalToken(token)) {
            throw "Attempted to resolve unregistered dependency token: " + token.toString();
        }
        if (registration) {
            return this.resolveRegistration(registration);
        }
        return this.construct(token);
    };
    InternalDependencyContainer.prototype.resolveRegistration = function (registration) {
        if (isValueProvider(registration.provider)) {
            return registration.provider.useValue;
        }
        else if (isTokenProvider(registration.provider)) {
            return registration.options.singleton
                ? registration.instance ||
                    (registration.instance = this.resolve(registration.provider.useToken))
                : this.resolve(registration.provider.useToken);
        }
        else if (isClassProvider(registration.provider)) {
            return registration.options.singleton
                ? registration.instance ||
                    (registration.instance = this.construct(registration.provider.useClass))
                : this.construct(registration.provider.useClass);
        }
        else if (isFactoryProvider(registration.provider)) {
            return registration.provider.useFactory(this);
        }
        else {
            return this.construct(registration.provider);
        }
    };
    InternalDependencyContainer.prototype.resolveAll = function (token) {
        var _this = this;
        var registration = this.getAllRegistrations(token);
        if (!registration && isNormalToken(token)) {
            throw "Attempted to resolve unregistered dependency token: " + token.toString();
        }
        if (registration) {
            return registration.map(function (item) { return _this.resolveRegistration(item); });
        }
        return [this.construct(token)];
    };
    InternalDependencyContainer.prototype.isRegistered = function (token) {
        return this._registry.has(token);
    };
    InternalDependencyContainer.prototype.reset = function () {
        this._registry.clear();
    };
    InternalDependencyContainer.prototype.createChildContainer = function () {
        return new InternalDependencyContainer(this);
    };
    InternalDependencyContainer.prototype.getRegistration = function (token) {
        if (this.isRegistered(token)) {
            return this._registry.get(token);
        }
        if (this.parent) {
            return this.parent.getRegistration(token);
        }
        return null;
    };
    InternalDependencyContainer.prototype.getAllRegistrations = function (token) {
        if (this.isRegistered(token)) {
            return this._registry.getAll(token);
        }
        if (this.parent) {
            return this.parent.getAllRegistrations(token);
        }
        return null;
    };
    InternalDependencyContainer.prototype.construct = function (ctor) {
        var _this = this;
        if (ctor.length === 0) {
            return new ctor();
        }
        var paramInfo = typeInfo.get(ctor);
        if (!paramInfo || paramInfo.length === 0) {
            throw "TypeInfo not known for " + ctor;
        }
        var params = paramInfo.map(function (param) {
            if (isTokenDescriptor(param)) {
                return param.multiple
                    ? _this.resolveAll(param.token)
                    : _this.resolve(param.token);
            }
            return _this.resolve(param);
        });
        return new (ctor.bind.apply(ctor, [void 0].concat(params)))();
    };
    return InternalDependencyContainer;
}());
export var instance = new InternalDependencyContainer();
export default instance;
